/* Admin orders page
   Updated: Jortt sync errors now shown directly in toast instead of console only;
   added client-side pagination (50 orders/page) to avoid rendering hundreds of rows at once.
   Updated: added a "Download factuur" button in the row actions between the mail
   and view buttons; downloads a PDF named after the order number (e.g. 2026-0077.pdf).
   Updated: Order details and delivery dialog show the chosen Bezorgdag (old orders keep their 3-day period).
   Updated: Downloaded invoice PDF now shows the Bezorgdag or pickup moment.
   Updated: mails now go through Brevo via secured server actions. A status change (with optional customer
   mail) and "opnieuw versturen" each use one action id, so double clicks or retries never send twice and
   saving the same status sends nothing. Resend asks for confirmation as a real customer mail. "Stuur test
   factuur" sends a fictitious Brevo test mail only to the fixed test address. EmailJS removed.
   Updated: admin-created invoices get a "Factuur versturen" button that opens the exact-preview send dialog;
   an already paid invoice is (re)sent as paid with the same invoice number.
   Updated: every status change (also cancellation) opens a dialog with Annuleren / Alleen status wijzigen /
   Status wijzigen en klant mailen, showing beforehand which customer mail will be sent. */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import {
  describeQueueResult, invokeBrevoMail, resendOrderMail, setOrderStatus, TEST_MAIL_RECIPIENT, type QueueResult,
} from '@/lib/transactional-mail';
import { toast } from 'sonner';
import Breadcrumb from '@/components/admin/Breadcrumb';
import StatusChangeDialog from '@/components/admin/StatusChangeDialog';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader as Loader2, Package, Eye, Truck, Calculator, Trash2, TrendingUp, Euro, ShoppingCart, Calendar, CircleCheck as CheckCircle, Clock, Circle as XCircle, ChevronLeft, ChevronRight, Mail, Send, Plus, ArrowLeft, Download, FileText } from 'lucide-react';
import DocumentSendDialog from '@/components/admin/document-stepper/DocumentSendDialog';
import { motion } from 'framer-motion';
import DocumentStepper from '@/components/admin/document-stepper/DocumentStepper';
import { describeOrderDelivery, formatDeliveryDay } from '@/lib/delivery-date';
import {
  createDocumentElement,
  generateDocumentPDFBlob,
  downloadDocumentPDF,
  type DocumentData,
  type DocumentItem,
} from '@/lib/document-generator';

interface Order {
  id: string;
  order_number: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  customer_address: string;
  customer_city: string;
  customer_postal_code: string;
  customer_notes: string | null;
  subtotal: number;
  tax: number;
  delivery_fee: number;
  total: number;
  status: string;
  payment_method: string;
  delivery_type: string;
  delivery_week: string | null;
  pickup_timeslot: string | null;
  calculated_distance_km: number | null;
  calculated_pallets: number | null;
  internal_delivery_cost: number | null;
  oegema_delivery_cost: number | null;
  selected_delivery_method: string | null;
  delivery_calculation_data: any | null;
  coupon_code: string | null;
  coupon_discount: number | null;
  source: string | null;
  admin_document_id: string | null;
  created_at: string;
  order_items?: OrderItem[];
}

interface OrderItem {
  id: string;
  product_name: string;
  variant_size: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  btw_percentage: number | null;
  product_variant_id: string | null;
  product_variants?: {
    sku: string | null;
  };
}

const statusColors: Record<string, string> = {
  pending: 'bg-[#c8a94a]',
  confirmed: 'bg-[#5a6552]',
  processing: 'bg-[#7a6450]',
  shipped: 'bg-[#a67c52]',
  delivered: 'bg-[#1e2b22]',
  cancelled: 'bg-[#a24a3d]',
};

const statusLabels: Record<string, string> = {
  pending: 'In afwachting',
  confirmed: 'Bevestigd',
  processing: 'In behandeling',
  shipped: 'Verzonden',
  delivered: 'Bezorgd',
  cancelled: 'Geannuleerd',
};

export default function OrdersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<Order | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeliveryModalOpen, setIsDeliveryModalOpen] = useState(false);
  const [selectedDeliveryOrder, setSelectedDeliveryOrder] = useState<Order | null>(null);
  const [nextOrderNumber, setNextOrderNumber] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [resendingEmailForOrder, setResendingEmailForOrder] = useState<string | null>(null);
  const [downloadingInvoiceForOrder, setDownloadingInvoiceForOrder] = useState<string | null>(null);
  const [invoiceSendDocumentId, setInvoiceSendDocumentId] = useState<string | null>(null);
  const [isSendingTestInvoice, setIsSendingTestInvoice] = useState(false);
  const [isSyncingJortt, setIsSyncingJortt] = useState(false);
  const [testInvoiceDialogOpen, setTestInvoiceDialogOpen] = useState(false);
  const [showStepper, setShowStepper] = useState(() => searchParams.get('new') === 'true');
  const [currentPage, setCurrentPage] = useState(1);
  const ORDERS_PER_PAGE = 50;

  useEffect(() => {
    if (searchParams.get('new') === 'true') {
      setShowStepper(true);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('new');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleStepperComplete = () => {
    setShowStepper(false);
    loadOrders();
    loadNextOrderNumber();
  };

  useEffect(() => {
    loadOrders();
    loadNextOrderNumber();
  }, []);

  useEffect(() => {
    const openId = searchParams.get('open');
    if (!openId || orders.length === 0) return;
    const order = orders.find(o => o.id === openId);
    if (order) {
      setSelectedOrder(order);
      setIsDetailsOpen(true);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('open');
      setSearchParams(newParams, { replace: true });
    }
  }, [orders, searchParams, setSearchParams]);

  const loadOrders = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          order_items (
            id,
            product_name,
            variant_size,
            quantity,
            unit_price,
            total_price,
            btw_percentage,
            product_variant_id,
            product_variants (
              sku
            )
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setOrders(data || []);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadNextOrderNumber = async () => {
    try {
      const { data, error } = await supabase.rpc('get_next_order_number');
      if (error) throw error;
      setNextOrderNumber(data);
    } catch (error) {
      console.error('Error loading next order number:', error);
    }
  };

  const [statusEmailOrder, setStatusEmailOrder] = useState<Order | null>(null);
  const [pendingStatus, setPendingStatus] = useState<string>('');
  const [isSendingStatusEmail, setIsSendingStatusEmail] = useState(false);

  const [statusActionId, setStatusActionId] = useState('');
  const [resendOrder, setResendOrder] = useState<Order | null>(null);
  const [resendActionId, setResendActionId] = useState('');

  const showQueueResult = (result: QueueResult, recipient: string) => {
    const msg = describeQueueResult(result, recipient);
    const opts = { description: msg.description, duration: 8000 };
    if (msg.kind === 'success') toast.success(msg.title, opts);
    else if (msg.kind === 'warning') toast.warning(msg.title, opts);
    else toast.info(msg.title, opts);
  };

  const updateOrderStatus = async (orderId: string, newStatus: string) => {
    const order = orders.find(o => o.id === orderId);
    if (!order || newStatus === order.status) return;
    setStatusEmailOrder(order);
    setPendingStatus(newStatus);
    setStatusActionId(crypto.randomUUID());
  };

  const applyOrderStatus = async (order: Order, newStatus: string, sendEmail: boolean, actionId: string) => {
    try {
      const result = await setOrderStatus(order.id, newStatus, sendEmail, actionId);
      showQueueResult(result, order.customer_email);
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error('Fout bij het bijwerken van de status', { description: error instanceof Error ? error.message : undefined });
    } finally {
      await loadOrders();
    }
  };

  const closeStatusDialog = () => {
    setStatusEmailOrder(null);
    setPendingStatus('');
    setStatusActionId('');
  };

  const confirmStatusEmail = async (sendEmail: boolean) => {
    if (!statusEmailOrder || !pendingStatus || !statusActionId) return;
    const order = statusEmailOrder;
    setIsSendingStatusEmail(true);
    try {
      await applyOrderStatus(order, pendingStatus, sendEmail, statusActionId);
    } finally {
      setIsSendingStatusEmail(false);
      closeStatusDialog();
    }
  };

  const handleDeleteClick = (order: Order) => {
    setOrderToDelete(order);
    setDeleteDialogOpen(true);
  };

  const deleteOrder = async () => {
    if (!orderToDelete) return;

    try {
      setIsDeleting(true);

      const { error: itemsError } = await supabase
        .from('order_items')
        .delete()
        .eq('order_id', orderToDelete.id);

      if (itemsError) throw itemsError;

      const { error: orderError } = await supabase
        .from('orders')
        .delete()
        .eq('id', orderToDelete.id);

      if (orderError) throw orderError;

      setDeleteDialogOpen(false);
      setOrderToDelete(null);
      await loadOrders();
    } catch (error) {
      console.error('Error deleting order:', error);
      alert('Fout bij het verwijderen van de bestelling');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const showOrderDetails = (order: Order) => {
    setSelectedOrder(order);
    setIsDetailsOpen(true);
  };

  const showDeliveryBreakdown = (order: Order) => {
    setSelectedDeliveryOrder(order);
    setIsDeliveryModalOpen(true);
  };

  const resendConfirmationEmail = (order: Order) => {
    setResendOrder(order);
    setResendActionId(crypto.randomUUID());
  };

  const confirmResend = async () => {
    if (!resendOrder || !resendActionId) return;
    const order = resendOrder;
    setResendingEmailForOrder(order.id);
    try {
      const result = await resendOrderMail(order.id, resendActionId);
      showQueueResult(result, order.customer_email);
    } catch (error) {
      console.error('Error resending email:', error);
      toast.error('Mail opnieuw versturen mislukt', { description: error instanceof Error ? error.message : undefined, duration: 10000 });
    } finally {
      setResendingEmailForOrder(null);
      setResendOrder(null);
      setResendActionId('');
    }
  };

  const downloadInvoicePDF = async (order: Order) => {
    if (!order.order_items || order.order_items.length === 0) {
      toast.error('Geen bestelitems gevonden voor deze bestelling');
      return;
    }

    setDownloadingInvoiceForOrder(order.id);
    let element: HTMLElement | null = null;
    try {
      const doc: DocumentData = {
        document_number: order.order_number,
        document_type: 'invoice',
        customer_name: order.customer_name,
        customer_email: order.customer_email,
        customer_phone: order.customer_phone,
        customer_address: order.customer_address,
        customer_city: order.customer_city,
        customer_postal_code: order.customer_postal_code,
        customer_notes: order.customer_notes || undefined,
        subtotal: parseFloat(order.subtotal.toString()),
        delivery_fee: parseFloat(order.delivery_fee.toString()),
        discount_amount: order.coupon_discount ? parseFloat(order.coupon_discount.toString()) : 0,
        total: parseFloat(order.total.toString()),
        created_at: order.created_at,
        delivery_info: describeOrderDelivery(order),
      };

      const items: DocumentItem[] = order.order_items.map((item) => ({
        description: `${item.product_name} - ${item.variant_size}`,
        quantity: item.quantity,
        unit_price: parseFloat(item.unit_price.toString()),
        total_price: parseFloat(item.total_price.toString()),
        btw_percentage: item.btw_percentage ?? 9,
      }));

      element = await createDocumentElement(doc, items);
      const base64 = await generateDocumentPDFBlob(element);
      downloadDocumentPDF(base64, `${order.order_number}.pdf`);
      toast.success(`Factuur ${order.order_number}.pdf gedownload`);
    } catch (error) {
      console.error('Error downloading invoice PDF:', error);
      const msg = error instanceof Error ? error.message : 'Onbekende fout';
      toast.error('Factuur kon niet worden gedownload', { description: msg, duration: 10000 });
    } finally {
      if (element && element.parentNode) {
        element.parentNode.removeChild(element);
      }
      setDownloadingInvoiceForOrder(null);
    }
  };

  const sendTestInvoice = useCallback(async () => {
    setIsSendingTestInvoice(true);
    try {
      const data = await invokeBrevoMail<{ job?: { status: string; last_error: string | null } }>({
        action: 'send_test', mail_type: 'order_pending_bank_transfer',
      });
      if (!data.job) throw new Error('Geen resultaat ontvangen');
      if (data.job.status === 'accepted') {
        toast.success(`Testfactuur geaccepteerd door Brevo`, { description: `Alleen verstuurd naar ${TEST_MAIL_RECIPIENT}.` });
        setTestInvoiceDialogOpen(false);
      } else {
        toast.warning(`Testmail niet geaccepteerd (${data.job.status})`, { description: data.job.last_error ?? undefined });
      }
    } catch (error) {
      console.error('Error sending test invoice:', error);
      toast.error('Fout bij het verzenden van de testfactuur', { description: error instanceof Error ? error.message : undefined, duration: 10000 });
    } finally {
      setIsSendingTestInvoice(false);
    }
  }, []);

  const getDeliveryMethodLabel = (order: Order) => {
    if (order.delivery_type === 'pickup') return 'Afhalen';
    if (!order.selected_delivery_method) return 'Bezorging';
    return order.selected_delivery_method === 'internal' ? 'Eigen levering' : 'Oegema';
  };

  const syncToJortt = async () => {
    setIsSyncingJortt(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Je moet ingelogd zijn om te synchroniseren');
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(
        `${supabaseUrl}/functions/v1/sync-invoices-to-jortt`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Sync mislukt');
      }

      const succeeded = result.results?.filter((r: any) => r.status === 'success').length || 0;
      const failed = result.results?.filter((r: any) => r.status === 'error').length || 0;
      const skipped = result.results?.filter((r: any) => r.status === 'skipped').length || 0;

      if (failed > 0) {
        const errors = result.results.filter((r: any) => r.status === 'error');
        const errorSummary = errors
          .slice(0, 5)
          .map((r: any) => `${r.order_number}: ${r.error}`)
          .join('\n');
        const moreText = errors.length > 5 ? `\n...en ${errors.length - 5} meer` : '';
        toast.warning(`${succeeded} gesynchroniseerd, ${failed} mislukt${skipped ? `, ${skipped} overgeslagen` : ''}:\n${errorSummary}${moreText}`, {
          duration: 15000,
        });
        console.error('Jortt sync errors:', errors);
      } else {
        toast.success(`${succeeded} bestellingen succesvol naar Jortt gesynchroniseerd!${skipped ? ` (${skipped} overgeslagen)` : ''}`);
      }
    } catch (error) {
      console.error('Jortt sync error:', error);
      toast.error(error instanceof Error ? error.message : 'Fout bij synchroniseren naar Jortt');
    } finally {
      setIsSyncingJortt(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const minYear = 2025;
  const maxYear = currentYear + 1;

  const canGoBack = selectedYear > minYear;
  const canGoForward = selectedYear < maxYear;

  // Filter orders by selected year
  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      const parts = order.order_number.split('-');
      let year: number;

      if (parts[0] === 'ORD' && parts[1]) {
        year = parseInt(parts[1].substring(0, 4));
      } else {
        year = parseInt(parts[0]);
      }

      return year === selectedYear;
    });
  }, [orders, selectedYear]);

  const totalPages = Math.ceil(filteredOrders.length / ORDERS_PER_PAGE);
  const paginatedOrders = useMemo(() => {
    const start = (currentPage - 1) * ORDERS_PER_PAGE;
    return filteredOrders.slice(start, start + ORDERS_PER_PAGE);
  }, [filteredOrders, currentPage]);

  useEffect(() => { setCurrentPage(1); }, [selectedYear]);

  // Calculate statistics for filtered orders
  const stats = useMemo(() => {
    const totalOrders = filteredOrders.length;
    const pending = filteredOrders.filter((o) => o.status === 'pending').length;
    const confirmed = filteredOrders.filter((o) => o.status === 'confirmed').length;
    const processing = filteredOrders.filter((o) => o.status === 'processing').length;
    const shipped = filteredOrders.filter((o) => o.status === 'shipped').length;
    const completed = filteredOrders.filter((o) => o.status === 'delivered').length;
    const cancelled = filteredOrders.filter((o) => o.status === 'cancelled').length;

    const activeOrders = pending + confirmed + processing + shipped;

    const revenue = filteredOrders
      .filter((o) => o.status !== 'cancelled')
      .reduce((sum, o) => sum + parseFloat(o.total.toString()), 0);

    const averageOrderValue = totalOrders > 0 ? revenue / (totalOrders - cancelled) : 0;

    return {
      total: totalOrders,
      pending,
      confirmed,
      processing,
      shipped,
      activeOrders,
      completed,
      cancelled,
      revenue,
      averageOrderValue,
    };
  }, [filteredOrders]);

  return (
    <>
      {showStepper ? (
        <div className="max-w-7xl mx-auto px-6 py-12">
          <Breadcrumb pageName="Nieuwe bestelling" />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-4xl font-bold text-jv-primary">Nieuwe bestelling invoeren</h1>
                <p className="text-sm text-[#5a6552] mt-1">Maak een factuur aan en verstuur deze naar de klant</p>
              </div>
              <Button
                variant="outline"
                onClick={() => setShowStepper(false)}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Terug naar overzicht
              </Button>
            </div>

            <DocumentStepper
              documentType="invoice"
              onComplete={handleStepperComplete}
              onCancel={() => setShowStepper(false)}
            />
          </motion.div>
        </div>
      ) : (
      <div className="max-w-7xl mx-auto px-6 py-12">
        <Breadcrumb pageName="Bestellingen" />
        {/* Header with Year Selector */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold text-jv-primary mb-2">Bestellingen</h1>
            <p className="text-[#5a6552]">Beheer alle klantbestellingen per boekjaar</p>
          </div>

          <div className="flex items-center gap-3">
          <Button
            onClick={() => setShowStepper(true)}
            className="bg-jv-primary hover:bg-jv-primary/90 gap-2 leading-none"
          >
            <Plus className="h-4 w-4" />
            Nieuwe bestelling
          </Button>

          {/* Test Invoice Button */}
          <Button
            variant="outline"
            onClick={() => setTestInvoiceDialogOpen(true)}
            className="border-2 border-[#e7e1d6] text-[#5a6552] hover:bg-[#5a6552] hover:text-white hover:border-[#5a6552] gap-2 leading-none"
          >
            <Send className="h-4 w-4" />
            Stuur test factuur
          </Button>

          <Button
            variant="outline"
            onClick={syncToJortt}
            disabled={isSyncingJortt}
            className="border-2 border-[#1e2b22]/20 text-[#1e2b22] hover:bg-[#1e2b22] hover:text-white hover:border-[#1e2b22] gap-2 leading-none"
          >
            {isSyncingJortt ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Euro className="h-4 w-4" />
            )}
            Sync naar Jortt
          </Button>

          {/* Year Navigator */}
          <div className="inline-flex h-10 items-center justify-center rounded-md p-1 text-muted-foreground bg-white border-2 border-jv-primary/20">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedYear(selectedYear - 1)}
              disabled={!canGoBack}
              className="h-8 w-8 p-0 hover:bg-jv-primary/10 disabled:opacity-30"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2 px-4 py-1.5 bg-jv-primary text-white rounded-md mx-1 min-w-[120px] justify-center">
              <Calendar className="h-4 w-4" />
              <span className="font-semibold">{selectedYear}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedYear(selectedYear + 1)}
              disabled={!canGoForward}
              className="h-8 w-8 p-0 hover:bg-jv-primary/10 disabled:opacity-30"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
          </div>
        </div>

        {/* Statistics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Total Orders */}
          <Card className="border border-[#e7e1d6] bg-white hover:border-[#1e2b22]/40 transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <div className="p-3 bg-[#e7e1d6] rounded-lg">
                  <ShoppingCart className="h-6 w-6 text-[#1e2b22]" />
                </div>
                <TrendingUp className="h-5 w-5 text-[#5a6552]" />
              </div>
              <div className="text-3xl font-bold text-[#1e2b22] mb-1">{stats.total}</div>
              <p className="text-sm text-[#5a6552]">Totaal bestellingen</p>
            </CardContent>
          </Card>

          {/* Active Orders */}
          <Card className="border border-[#e7e1d6] bg-white hover:border-[#1e2b22]/40 transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <div className="p-3 bg-[#e7e1d6] rounded-lg">
                  <Clock className="h-6 w-6 text-[#5a6552]" />
                </div>
              </div>
              <div className="text-3xl font-bold text-[#1e2b22] mb-1">{stats.activeOrders}</div>
              <p className="text-sm text-[#5a6552]">Actieve bestellingen</p>
              <p className="text-xs text-[#8a8377] mt-1">
                {stats.pending} in afwachting, {stats.processing + stats.confirmed} in behandeling
              </p>
            </CardContent>
          </Card>

          {/* Completed Orders */}
          <Card className="border border-[#e7e1d6] bg-white hover:border-[#1e2b22]/40 transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <div className="p-3 bg-[#e7e1d6] rounded-lg">
                  <CheckCircle className="h-6 w-6 text-[#1e2b22]" />
                </div>
              </div>
              <div className="text-3xl font-bold text-[#1e2b22] mb-1">{stats.completed}</div>
              <p className="text-sm text-[#5a6552]">Afgeleverd</p>
              {stats.cancelled > 0 && (
                <p className="text-xs text-[#a24a3d] mt-1">{stats.cancelled} geannuleerd</p>
              )}
            </CardContent>
          </Card>

          {/* Revenue */}
          <Card className="border border-[#e7e1d6] bg-white hover:border-[#1e2b22]/40 transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <div className="p-3 bg-[#e7e1d6] rounded-lg">
                  <Euro className="h-6 w-6 text-[#1e2b22]" />
                </div>
              </div>
              <div className="text-3xl font-bold text-[#1e2b22] mb-1">{formatPrice(stats.revenue)}</div>
              <p className="text-sm text-[#5a6552]">Totale omzet</p>
              <p className="text-xs text-[#8a8377] mt-1">
                Gem. {formatPrice(stats.averageOrderValue)} per bestelling
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Orders Table */}
        <Card className="border border-[#e7e1d6] bg-white">
          <CardHeader className="bg-[#fcfaf6] border-b border-[#e7e1d6]">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl text-[#1e2b22] flex items-center gap-2">
                  <Package className="h-6 w-6" />
                  Bestellingen {selectedYear}
                </CardTitle>
                <p className="text-sm text-[#5a6552] mt-1">
                  {filteredOrders.length} bestelling{filteredOrders.length !== 1 ? 'en' : ''} in dit boekjaar
                </p>
              </div>
              {nextOrderNumber && (
                <div className="text-right">
                  <p className="text-xs text-[#5a6552] uppercase tracking-wide">Volgend bestelnummer</p>
                  <p className="text-lg font-mono font-bold text-[#1e2b22]">{nextOrderNumber}</p>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="h-10 w-10 animate-spin text-[#1e2b22] mb-4" />
                <p className="text-[#5a6552]">Bestellingen laden...</p>
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="p-4 bg-[#e7e1d6] rounded-full mb-4">
                  <Package className="h-12 w-12 text-[#5a6552]" />
                </div>
                <p className="text-xl font-semibold text-[#1e2b22] mb-2">
                  Geen bestellingen in {selectedYear}
                </p>
                <p className="text-[#5a6552]">
                  Er zijn nog geen bestellingen geplaatst in dit boekjaar
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#fcfaf6] border-b border-[#e7e1d6]">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a6552] uppercase tracking-wider">
                        Bestelnummer
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a6552] uppercase tracking-wider">
                        Klant
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a6552] uppercase tracking-wider">
                        Datum
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a6552] uppercase tracking-wider">
                        Totaal
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a6552] uppercase tracking-wider">
                        Levering
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a6552] uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a6552] uppercase tracking-wider">
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#e7e1d6] bg-white">
                    {paginatedOrders.map((order) => (
                      <tr key={order.id} className="hover:bg-[#e7e1d6]/30 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm font-semibold text-[#1e2b22]">
                              {order.order_number}
                            </span>
                            {order.source === 'admin' && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-[#7a6450] text-[#7a6450] bg-[#f5eee0]">
                                Factuur
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-semibold text-[#1e2b22]">
                            {order.customer_name}
                          </div>
                          <div className="text-sm text-[#5a6552]">{order.customer_email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-[#1e2b22]">
                            {new Date(order.created_at).toLocaleDateString('nl-NL', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </div>
                          <div className="text-xs text-[#5a6552]">
                            {new Date(order.created_at).toLocaleTimeString('nl-NL', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-base font-bold text-[#1e2b22]">
                            {formatPrice(parseFloat(order.total.toString()))}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => showDeliveryBreakdown(order)}
                            className="inline-flex items-center gap-1 text-sm text-[#5a6552] hover:text-[#1e2b22] hover:underline font-medium"
                          >
                            <Truck className="h-4 w-4" />
                            {getDeliveryMethodLabel(order)}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Select
                            value={order.status}
                            onValueChange={(value) => updateOrderStatus(order.id, value)}
                          >
                            <SelectTrigger className="w-[160px] border-2">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.keys(statusLabels).map((status) => (
                                <SelectItem key={status} value={status}>
                                  <div className="flex items-center gap-2">
                                    <div
                                      className={`h-2.5 w-2.5 rounded-full ${statusColors[status]}`}
                                    />
                                    {statusLabels[status]}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => resendConfirmationEmail(order)}
                              disabled={resendingEmailForOrder === order.id}
                              className="border border-[#e7e1d6] text-[#5a6552] hover:bg-[#5a6552] hover:text-white hover:border-[#5a6552]"
                              title="Bevestigingsmail opnieuw verzenden"
                            >
                              {resendingEmailForOrder === order.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Mail className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => downloadInvoicePDF(order)}
                              disabled={downloadingInvoiceForOrder === order.id}
                              className="border border-[#e7e1d6] text-[#5a6552] hover:bg-[#5a6552] hover:text-white hover:border-[#5a6552]"
                              title={`Factuur downloaden (${order.order_number}.pdf)`}
                            >
                              {downloadingInvoiceForOrder === order.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Download className="h-4 w-4" />
                              )}
                            </Button>
                            {order.admin_document_id && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setInvoiceSendDocumentId(order.admin_document_id)}
                                className="border border-[#e7e1d6] text-[#5a6552] hover:bg-[#5a6552] hover:text-white hover:border-[#5a6552]"
                                title={`Factuur ${order.order_number} versturen (voorbeeld eerst)`}
                              >
                                <FileText className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => showOrderDetails(order)}
                              className="border border-[#1e2b22]/30 text-[#1e2b22] hover:bg-[#1e2b22] hover:text-white"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {order.status === 'cancelled' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteClick(order)}
                                className="border border-[#a24a3d]/40 text-[#a24a3d] hover:bg-[#a24a3d] hover:text-white"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-[#e7e1d6] px-6 py-4">
              <p className="text-sm text-[#5a6552]">
                Pagina {currentPage} van {totalPages} ({filteredOrders.length} totaal)
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" /> Vorige
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Volgende <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
      )}

      {/* Order Details Dialog */}
      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Besteldetails - {selectedOrder?.order_number}</DialogTitle>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge className={statusColors[selectedOrder.status]}>
                    {statusLabels[selectedOrder.status]}
                  </Badge>
                  <span className="text-sm text-gray-600">
                    {formatDate(selectedOrder.created_at)}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => resendConfirmationEmail(selectedOrder)}
                  disabled={resendingEmailForOrder === selectedOrder.id}
                  className="border border-[#e7e1d6] text-[#5a6552] hover:bg-[#5a6552] hover:text-white hover:border-[#5a6552] gap-2"
                >
                  {resendingEmailForOrder === selectedOrder.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  E-mail opnieuw verzenden
                </Button>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-jv-primary mb-3">Klantgegevens</h3>
                  <div className="space-y-2 text-sm">
                    <p>
                      <span className="font-medium">Naam:</span> {selectedOrder.customer_name}
                    </p>
                    <p>
                      <span className="font-medium">Email:</span> {selectedOrder.customer_email}
                    </p>
                    <p>
                      <span className="font-medium">Telefoon:</span> {selectedOrder.customer_phone}
                    </p>
                    <p>
                      <span className="font-medium">Adres:</span> {selectedOrder.customer_address}
                    </p>
                    <p>
                      <span className="font-medium">Postcode:</span>{' '}
                      {selectedOrder.customer_postal_code}
                    </p>
                    <p>
                      <span className="font-medium">Plaats:</span> {selectedOrder.customer_city}
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-jv-primary mb-3">Betalingsinformatie</h3>
                  <div className="space-y-2 text-sm">
                    <p>
                      <span className="font-medium">Methode:</span> Bankoverschrijving
                    </p>
                    {selectedOrder.delivery_fee > 0 && (
                      <p>
                        <span className="font-medium">Bezorgkosten (incl. BTW):</span>{' '}
                        {formatPrice(parseFloat(selectedOrder.delivery_fee.toString()))}
                      </p>
                    )}
                    <p className="text-lg font-bold text-jv-accent">
                      <span className="font-medium">Totaal (incl. BTW):</span>{' '}
                      {formatPrice(parseFloat(selectedOrder.total.toString()))}
                    </p>
                  </div>
                </div>
              </div>

              {selectedOrder.delivery_type && (
                <div>
                  <h3 className="font-semibold text-jv-primary mb-3 flex items-center gap-2">
                    <Truck className="h-5 w-5" />
                    Bezorginformatie
                  </h3>
                  <div className="bg-[#fcfaf6] border border-[#e7e1d6] p-4 rounded-lg space-y-3">
                    <div className="flex items-start gap-2">
                      <div className="flex-1 space-y-2 text-sm">
                        <p>
                          <span className="font-medium">Type:</span>{' '}
                          {selectedOrder.delivery_type === 'delivery' ? 'Bezorging' : 'Afhalen'}
                        </p>

                        {selectedOrder.delivery_type === 'delivery' && selectedOrder.delivery_week && (
                          <p>
                            <span className="font-medium">Bezorgdag:</span> {formatDeliveryDay(selectedOrder.delivery_week, selectedOrder.created_at)}
                          </p>
                        )}

                        {selectedOrder.delivery_type === 'pickup' && selectedOrder.pickup_timeslot && (
                          <p>
                            <span className="font-medium">Afhaaltijdstip:</span> {selectedOrder.pickup_timeslot}
                          </p>
                        )}

                        {selectedOrder.calculated_pallets !== null && selectedOrder.calculated_pallets > 0 && (
                          <div className="border-t border-[#e7e1d6] pt-2 mt-2">
                            <p className="font-semibold text-[#1e2b22] mb-1 flex items-center gap-1">
                              <Calculator className="h-4 w-4" />
                              Berekening details
                            </p>
                            <div className="space-y-1 text-[#5a6552]">
                              <p>Aantal pallets: {selectedOrder.calculated_pallets}</p>
                              {selectedOrder.calculated_distance_km && (
                                <p>Afstand: {selectedOrder.calculated_distance_km} km (retour)</p>
                              )}
                              {selectedOrder.internal_delivery_cost && (
                                <p>Interne bezorging: {formatPrice(parseFloat(selectedOrder.internal_delivery_cost.toString()))}</p>
                              )}
                              {selectedOrder.oegema_delivery_cost && (
                                <p>Oegema transport: {formatPrice(parseFloat(selectedOrder.oegema_delivery_cost.toString()))}</p>
                              )}
                              {selectedOrder.selected_delivery_method && (
                                <p className="font-semibold text-[#1e2b22] mt-1">
                                  Gekozen methode: {selectedOrder.selected_delivery_method === 'internal' ? 'Interne bezorging' : 'Oegema transport'}
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {selectedOrder.customer_notes && (
                <div>
                  <h3 className="font-semibold text-jv-primary mb-2">Opmerkingen</h3>
                  <p className="text-sm text-[#5a6552] bg-[#fcfaf6] border border-[#e7e1d6] p-3 rounded">
                    {selectedOrder.customer_notes}
                  </p>
                </div>
              )}

              <div>
                <h3 className="font-semibold text-jv-primary mb-3">Bestelde producten</h3>
                <div className="space-y-3">
                  {selectedOrder.order_items?.map((item) => (
                    <div
                      key={item.id}
                      className="flex justify-between items-center p-3 bg-[#fcfaf6] border border-[#e7e1d6] rounded"
                    >
                      <div>
                        <p className="font-medium">{item.product_name}</p>
                        <p className="text-sm text-gray-600">
                          Maat: {item.variant_size} | Aantal: {item.quantity}
                          {item.product_variants?.sku && (
                            <> | Productnummer: <span className="font-mono">{item.product_variants.sku}</span></>
                          )}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-jv-primary">
                          {formatPrice(parseFloat(item.total_price.toString()))}
                        </p>
                        <p className="text-sm text-gray-600">
                          {formatPrice(parseFloat(item.unit_price.toString()))} per stuk
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Bestelling verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je bestelling <span className="font-mono font-semibold">{orderToDelete?.order_number}</span> wilt verwijderen?
              <br />
              <br />
              Deze actie kan niet ongedaan worden gemaakt. Alle bestelgegevens en items worden permanent verwijderd.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteOrder}
              disabled={isDeleting}
              className="bg-[#a24a3d] hover:bg-[#8a3a2f]"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verwijderen...
                </>
              ) : (
                'Verwijderen'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delivery Breakdown Modal */}
      <Dialog open={isDeliveryModalOpen} onOpenChange={setIsDeliveryModalOpen}>
        <DialogContent className="max-w-2xl" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5" />
              Leveringsberekening - {selectedDeliveryOrder?.order_number}
            </DialogTitle>
          </DialogHeader>

          {selectedDeliveryOrder && (
            <div className="space-y-6">
              {/* Delivery Type */}
              <div className="bg-[#fcfaf6] border border-[#e7e1d6] rounded-lg p-4">
                <h3 className="font-semibold text-[#1e2b22] mb-2">Leveringsmethode</h3>
                <p className="text-lg font-bold text-[#1e2b22]">
                  {selectedDeliveryOrder.delivery_type === 'pickup' ? 'Afhalen' : 'Bezorging'}
                </p>
                {selectedDeliveryOrder.delivery_type === 'pickup' && selectedDeliveryOrder.pickup_timeslot && (
                  <p className="text-sm text-[#5a6552] mt-1">
                    Afhaaltijdstip: {selectedDeliveryOrder.pickup_timeslot}
                  </p>
                )}
                {selectedDeliveryOrder.delivery_type === 'delivery' && selectedDeliveryOrder.delivery_week && (
                  <p className="text-sm text-[#5a6552] mt-1">
                    Bezorgdag: {formatDeliveryDay(selectedDeliveryOrder.delivery_week, selectedDeliveryOrder.created_at)}
                  </p>
                )}
              </div>

              {/* Only show delivery calculation if it's a delivery order */}
              {selectedDeliveryOrder.delivery_type === 'delivery' && (
                <>
                  {/* Calculation Details */}
                  {selectedDeliveryOrder.calculated_pallets !== null && selectedDeliveryOrder.calculated_pallets > 0 && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <Calculator className="h-5 w-5" />
                        Berekeningsgegevens
                      </h3>
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Aantal pallets:</span>
                          <span className="font-semibold">{selectedDeliveryOrder.calculated_pallets}</span>
                        </div>
                        {selectedDeliveryOrder.calculated_distance_km && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Afstand (retour):</span>
                            <span className="font-semibold">{selectedDeliveryOrder.calculated_distance_km} km</span>
                          </div>
                        )}

                        {/* Detailed breakdown if available */}
                        {selectedDeliveryOrder.delivery_calculation_data?.breakdown && (
                          <>
                            {/* Items by height breakdown */}
                            {selectedDeliveryOrder.delivery_calculation_data.breakdown.items_by_height &&
                             selectedDeliveryOrder.delivery_calculation_data.breakdown.items_by_height.length > 0 && (
                              <div className="border-t border-gray-300 pt-3 mt-3">
                                <p className="font-semibold text-gray-900 mb-2">Palletberekening per hoogtecategorie:</p>
                                <div className="space-y-2">
                                  {selectedDeliveryOrder.delivery_calculation_data.breakdown.items_by_height.map((item: any, idx: number) => (
                                    <div key={idx} className="bg-white p-2 rounded border border-gray-200">
                                      <div className="flex justify-between text-xs">
                                        <span className="text-gray-700">Hoogte: {item.height_range}</span>
                                        <span className="font-medium text-gray-900">{item.pallets_needed} pallet(s)</span>
                                      </div>
                                      <div className="text-xs text-gray-500 mt-1">
                                        {item.count} plant(en) × {item.plants_per_pallet} per pallet
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Internal calculation breakdown */}
                            {selectedDeliveryOrder.delivery_calculation_data.breakdown.internal_calculation && (
                              <div className="border-t border-gray-300 pt-3 mt-3">
                                <p className="font-semibold text-gray-900 mb-2">Eigen levering berekening:</p>
                                <div className="space-y-2 text-xs">
                                  <div className="bg-[#fcfaf6] border-2 border-[#e7e1d6] p-3 rounded">
                                    <div className="font-semibold text-[#1e2b22] mb-2">Volledige som:</div>
                                    <div className="font-mono text-xs bg-white p-2 rounded border border-[#e7e1d6] break-all">
                                      max({selectedDeliveryOrder.delivery_calculation_data.breakdown.internal_calculation.distance_km} km × {formatPrice((selectedDeliveryOrder.delivery_calculation_data.breakdown.internal_calculation.base_cost / selectedDeliveryOrder.delivery_calculation_data.breakdown.internal_calculation.distance_km))}/km, {formatPrice(selectedDeliveryOrder.delivery_calculation_data.breakdown.internal_calculation.minimum_fee)}) = max({formatPrice(selectedDeliveryOrder.delivery_calculation_data.breakdown.internal_calculation.base_cost)}, {formatPrice(selectedDeliveryOrder.delivery_calculation_data.breakdown.internal_calculation.minimum_fee)}) = <span className="font-bold text-[#1e2b22]">{formatPrice(selectedDeliveryOrder.delivery_calculation_data.breakdown.internal_calculation.final_cost)}</span>
                                    </div>
                                  </div>

                                  <div className="bg-white p-2 rounded border border-gray-200">
                                    <div className="space-y-1">
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Afstand (retour):</span>
                                        <span className="font-medium">{selectedDeliveryOrder.delivery_calculation_data.breakdown.internal_calculation.distance_km} km</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Prijs per km:</span>
                                        <span className="font-medium">{formatPrice((selectedDeliveryOrder.delivery_calculation_data.breakdown.internal_calculation.base_cost / selectedDeliveryOrder.delivery_calculation_data.breakdown.internal_calculation.distance_km))}</span>
                                      </div>
                                      <div className="flex justify-between pt-1 border-t border-gray-200">
                                        <span className="text-gray-700">Basiskosten:</span>
                                        <span className="font-semibold">{formatPrice(selectedDeliveryOrder.delivery_calculation_data.breakdown.internal_calculation.base_cost)}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Minimum bedrag:</span>
                                        <span className="font-medium">{formatPrice(selectedDeliveryOrder.delivery_calculation_data.breakdown.internal_calculation.minimum_fee)}</span>
                                      </div>
                                    </div>
                                  </div>

                                  <div className="flex justify-between font-semibold bg-[#e7e1d6]/60 p-2 rounded border-2 border-[#1e2b22]/40">
                                    <span className="text-[#1e2b22]">Eindtotaal:</span>
                                    <span className="text-[#1e2b22]">{formatPrice(selectedDeliveryOrder.delivery_calculation_data.breakdown.internal_calculation.final_cost)}</span>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Oegema calculation breakdown */}
                            {selectedDeliveryOrder.delivery_calculation_data.breakdown.oegema_calculation && (
                              <div className="border-t border-gray-300 pt-3 mt-3">
                                <p className="font-semibold text-gray-900 mb-2">Oegema berekening:</p>
                                <div className="space-y-2 text-xs">
                                  <div className="bg-[#fcfaf6] border-2 border-[#e7e1d6] p-3 rounded">
                                    <div className="font-semibold text-[#1e2b22] mb-2">Volledige som:</div>
                                    <div className="font-mono text-xs bg-white p-2 rounded border border-[#e7e1d6] break-all">
                                      Vaste prijs voor {selectedDeliveryOrder.delivery_calculation_data.breakdown.oegema_calculation.pallets || selectedDeliveryOrder.delivery_calculation_data.breakdown.oegema_calculation.base_pallets} pallet{(selectedDeliveryOrder.delivery_calculation_data.breakdown.oegema_calculation.pallets || selectedDeliveryOrder.delivery_calculation_data.breakdown.oegema_calculation.base_pallets) !== 1 ? 's' : ''} = <span className="font-bold text-[#1e2b22]">{formatPrice(selectedDeliveryOrder.delivery_calculation_data.breakdown.oegema_calculation.final_cost)}</span>
                                    </div>
                                  </div>

                                  <div className="bg-white p-2 rounded border border-gray-200">
                                    <div className="space-y-1">
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Aantal pallets:</span>
                                        <span className="font-medium">{selectedDeliveryOrder.delivery_calculation_data.breakdown.oegema_calculation.pallets || selectedDeliveryOrder.delivery_calculation_data.breakdown.oegema_calculation.base_pallets}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Tarief:</span>
                                        <span className="font-semibold">{formatPrice(selectedDeliveryOrder.delivery_calculation_data.breakdown.oegema_calculation.base_cost || selectedDeliveryOrder.delivery_calculation_data.breakdown.oegema_calculation.final_cost)}</span>
                                      </div>
                                    </div>
                                  </div>

                                  <div className="flex justify-between font-semibold bg-[#e7e1d6]/60 p-2 rounded border-2 border-[#1e2b22]/40">
                                    <span className="text-[#1e2b22]">Eindtotaal:</span>
                                    <span className="text-[#1e2b22]">{formatPrice(selectedDeliveryOrder.delivery_calculation_data.breakdown.oegema_calculation.final_cost)}</span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Cost Comparison */}
                  <div className="space-y-3">
                    <h3 className="font-semibold text-gray-900">Kostenvergelijking</h3>

                    <div className="grid md:grid-cols-2 gap-3">
                      {/* Internal Delivery */}
                      <div className={`border-2 rounded-lg p-4 ${
                        selectedDeliveryOrder.selected_delivery_method === 'internal'
                          ? 'border-[#1e2b22] bg-[#e7e1d6]/40'
                          : 'border-[#e7e1d6] bg-white'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-[#1e2b22]">Eigen levering</h4>
                          {selectedDeliveryOrder.selected_delivery_method === 'internal' && (
                            <Badge className="bg-[#1e2b22]">Gekozen</Badge>
                          )}
                        </div>
                        <div className="text-2xl font-bold text-[#1e2b22]">
                          {selectedDeliveryOrder.internal_delivery_cost !== null
                            ? formatPrice(parseFloat(selectedDeliveryOrder.internal_delivery_cost.toString()))
                            : 'N.v.t.'}
                        </div>
                        <p className="text-xs text-[#5a6552] mt-1">Excl. BTW</p>
                      </div>

                      {/* Oegema Delivery */}
                      <div className={`border-2 rounded-lg p-4 ${
                        selectedDeliveryOrder.selected_delivery_method === 'oegema'
                          ? 'border-[#1e2b22] bg-[#e7e1d6]/40'
                          : 'border-[#e7e1d6] bg-white'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-[#1e2b22]">Oegema</h4>
                          {selectedDeliveryOrder.selected_delivery_method === 'oegema' && (
                            <Badge className="bg-[#1e2b22]">Gekozen</Badge>
                          )}
                        </div>
                        <div className="text-2xl font-bold text-[#1e2b22]">
                          {selectedDeliveryOrder.oegema_delivery_cost !== null
                            ? formatPrice(parseFloat(selectedDeliveryOrder.oegema_delivery_cost.toString()))
                            : 'N.v.t.'}
                        </div>
                        <p className="text-xs text-[#5a6552] mt-1">Excl. BTW</p>
                      </div>
                    </div>
                  </div>

                  {/* Final Delivery Fee */}
                  <div className="bg-[#e7e1d6]/50 border border-[#5a6552]/40 rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-[#1e2b22]">Bezorgkosten aan klant:</span>
                      <span className="text-2xl font-bold text-[#1e2b22]">
                        {formatPrice(parseFloat(selectedDeliveryOrder.delivery_fee.toString()))}
                      </span>
                    </div>
                    <p className="text-xs text-[#5a6552] mt-1">Incl. BTW</p>
                  </div>
                </>
              )}

              {/* Customer Address - Only show for delivery orders */}
              {selectedDeliveryOrder.delivery_type === 'delivery' && (
                <div className="bg-[#fcfaf6] border border-[#e7e1d6] rounded-lg p-4">
                  <h3 className="font-semibold text-[#1e2b22] mb-2">Afleveradres</h3>
                  <div className="text-sm text-[#5a6552] space-y-1">
                    <p>{selectedDeliveryOrder.customer_address}</p>
                    <p>{selectedDeliveryOrder.customer_postal_code} {selectedDeliveryOrder.customer_city}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <StatusChangeDialog
        order={statusEmailOrder}
        newStatus={pendingStatus}
        statusLabel={statusLabels[pendingStatus] || pendingStatus}
        busy={isSendingStatusEmail}
        onCancel={closeStatusDialog}
        onConfirm={confirmStatusEmail}
      />

      <DocumentSendDialog
        documentId={invoiceSendDocumentId}
        resend
        onClose={() => setInvoiceSendDocumentId(null)}
      />

      {/* Resend Customer Mail Dialog */}
      <AlertDialog open={!!resendOrder} onOpenChange={(open) => { if (!open && !resendingEmailForOrder) { setResendOrder(null); setResendActionId(''); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Klantmail opnieuw versturen?</AlertDialogTitle>
            <AlertDialogDescription>
              Er wordt een <strong>echte klantmail</strong> voor bestelling {resendOrder?.order_number} met status{' '}
              <strong>{resendOrder ? statusLabels[resendOrder.status] || resendOrder.status : ''}</strong> opnieuw verstuurd naar{' '}
              {resendOrder?.customer_email}, met kopie naar info@buitnplanten.nl.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!resendingEmailForOrder}>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmResend(); }} disabled={!!resendingEmailForOrder}>
              {resendingEmailForOrder ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Versturen...</>
              ) : (
                <><Send className="h-4 w-4 mr-2" /> Klantmail versturen</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Test Invoice Email Dialog */}
      <Dialog open={testInvoiceDialogOpen} onOpenChange={setTestInvoiceDialogOpen}>
        <DialogContent className="max-w-md" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Testfactuur versturen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-[#5a6552] leading-relaxed">
              Er wordt een <strong>testmail</strong> met een fictieve bestelling en factuur verstuurd via Brevo,
              uitsluitend naar <strong>{TEST_MAIL_RECIPIENT}</strong>. Het onderwerp begint met [TEST] en er gaat
              geen kopie naar klanten of info@buitnplanten.nl.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setTestInvoiceDialogOpen(false)}
              disabled={isSendingTestInvoice}
            >
              Annuleren
            </Button>
            <Button
              onClick={sendTestInvoice}
              disabled={isSendingTestInvoice}
              className="bg-jv-primary hover:bg-jv-primary/90 gap-2"
            >
              {isSendingTestInvoice ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Versturen...</>
              ) : (
                <><Send className="h-4 w-4" /> Testmail versturen</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
